import { useEffect, useRef, useState } from "react";
import { validateFields } from "../utils/formSchema";
import NOA_FIELDS_JSON from "../data/noaFormFields";
import CONTRACT_FIELDS_JSON from "../data/contractFormFields";
import { noaDefaultFor, contractDefaultFor } from "../data/postApplicationDefaults";
import { financierDisplayName } from "../api/configApi";
import { savePostApplicationDraft, getPostApplicationDraft, clearPostApplicationDraft } from "../api/session";
import {
  saveContract as saveContractApi,
  saveNoa as saveNoaApi,
  extractNoa as extractNoaApi,
  getLeadDetails,
  getSentToFinancier,
  saveSentToFinancier,
  uploadDoc as uploadDocApi,
  removeDocs,
} from "../api/leadApi";
import { isApiSuccess } from "../utils/apiResponse";
import { uploadFileToStorage } from "../api/commonServiceApi";
import { templateToText, textToHtml } from "../utils/emailTemplate";
import { stageState, withStageDone } from "../utils/tabStatus";
import { CAP } from "../data/mockData";

// noa_received tab's form_data → NOA form values (everything as strings, the way the
// inputs hold them; dates trimmed to YYYY-MM-DD). Nulls are skipped.
const NOA_DATE_FIELDS = ["noa_issue_date", "approval_valid_until", "decline_date"];
const noaFromApi = (d) => {
  const out = {};
  if (d.noa_received) out.noa_received = d.noa_received;
  Object.entries(d.form_data || {}).forEach(([k, val]) => {
    if (val == null || val === "") return;
    out[k] = NOA_DATE_FIELDS.includes(k) ? String(val).slice(0, 10) : String(val);
  });
  return out;
};

// Post Approval Deadline (Days) is read-only: calendar days from today
// (Asia/Manila, same basis save_noa uses server-side) to Approval Valid Until,
// clamped at 0 once the date has passed; "" when no date is picked.
const daysUntil = (validUntil) => {
  const target = Date.parse(String(validUntil || "").slice(0, 10));
  if (Number.isNaN(target)) return "";
  const today = Date.parse(new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Manila" }));
  return String(Math.max(0, Math.round((target - today) / 86400000)));
};
const withDeadlineDays = (values) =>
  values && values.approval_valid_until ? { ...values, post_approval_deadline_days: daysUntil(values.approval_valid_until) } : values;

// NOA form values → save_noa payload. financier_id in the form is the bank's
// display name (read-only field), so the numeric id is passed in separately;
// numeric/flag fields go out as numbers, which is what SaveNoaDto validates.
const NOA_NUMBER_FIELDS = [
  "approved_loan_amount",
  "loan_term_years",
  "interest_rate",
  "monthly_amortization",
  "insurance_fire_excluded_flag",
  "bank_fees_amount",
  "post_approval_requirements_required",
  "post_approval_deadline_days",
];
const noaPayload = (leadId, financierId, values) => {
  const payload = { lead_id: leadId, financier_id: financierId, noa_received: values.noa_received };
  if (values.noa_received !== "Yes") {
    payload.decline_remark = values.decline_remark;
    payload.decline_date = values.decline_date;
    return payload;
  }
  NOA_FIELDS_JSON.forEach((f) => {
    if (!f.name || f.name === "noa_received" || f.name === "financier_id") return;
    if (f.field_show !== "noa_received/Yes") return;
    const val = values[f.name];
    if (val == null || val === "") return;
    payload[f.name] = NOA_NUMBER_FIELDS.includes(f.name) ? Number(val) : val;
  });
  return payload;
};

// contract_signed_details tab (SFA shape) → contract form values. contract_signing_received is null
// while pending, so the form keeps its own default ("Yes") in that case.
const contractFromApi = (d) => {
  const out = {};
  if (d.contract_signing_received) out.contract_signing_received = d.contract_signing_received;
  if (d.days_since_approval != null) out.days_since_approval = String(d.days_since_approval);
  if (d.contract_signing_date) out.contract_signing_date = String(d.contract_signing_date).slice(0, 10);
  if (d.contract_signed_docs) out.contract_signed_docs = d.contract_signed_docs;
  if (d.decline_remark) out.decline_remark = d.decline_remark;
  return out;
};

const todayIso = () => {
  const d = new Date();
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
};
const isoDate = (val) => (val ? String(val).slice(0, 10) : "");

// document_categories (root sections → child doc types → uploaded images) →
// the same category → requirement → files shape as the Upload Documents step:
// one entry per doc type, flagged Required / Optional (is_required_app,
// falling back to is_required), with the files uploaded against it. Doc types
// hidden from the app (show_in_app 0, e.g. co-borrower IDs) only show when
// something was actually uploaded for them, and are never enforced.
const sendFileFrom = (img, name, section, docId) => {
  const url = img.signed_url || img.doc_path || null;
  return {
    id: String(img.id),
    // document_list doc_id — what sent_to_financier's doc_ids takes (not the
    // rem_lead_docs row id above).
    docId: img.doc_id ?? docId,
    name,
    section,
    docType: img.doc_type || "",
    prevUrl: url,
    kind: /\.pdf($|\?)/i.test(url || "") ? "PDF" : "IMG",
    prev: "placeholder-document-doc.png",
  };
};
// Doc types with special handling on the Send page (document_list ids, as in
// useDocuments): Valid Government IDs need a primary/secondary ID type, so they
// are only managed from the Upload Documents step; the Home Loan Application
// Form is one per financier, so only this bank's form counts and it takes a
// single file.
const HL_FORM_DOC_ID = 2;
const GOV_ID_DOC_ID = 3;
const sendCatsFrom = (categories, financierKey) =>
  (categories || [])
    .map((cat) => {
      const section = cat.doclang?.name || cat.doc_section || "Documents";
      // Files uploaded against the section itself get a row of their own.
      const nodes = [...((cat.images || []).length ? [cat] : []), ...(cat.child || [])];
      const reqs = nodes
        .map((node) => {
          const name = node.doclang?.name || "Document";
          const hidden = node.show_in_app === 0;
          const hlForm = Number(node.id) === HL_FORM_DOC_ID;
          const images = (node.images || []).filter(
            (img) => !hlForm || img.financier_id == null || String(img.financier_id) === String(financierKey)
          );
          return {
            id: "req-" + node.id,
            docId: node.id,
            parentId: node === cat ? null : node.parent_id ?? cat.id,
            name,
            required: !hidden && node !== cat && !!(node.is_required_app ?? node.is_required),
            // Uploads allowed per doc type (max_upload), same default as the Upload Documents step.
            cap: hlForm ? 1 : Number(node.max_upload) || CAP,
            // false → no upload / remove here, files are view-only.
            editable: Number(node.id) !== GOV_ID_DOC_ID,
            files: images.map((img) => sendFileFrom(img, name, section, node.id)),
            hidden,
          };
        })
        .filter((r) => !r.hidden || r.files.length);
      return { key: String(cat.id), name: section, reqs };
    })
    .filter((c) => c.reqs.length);

// Flat list the send flow works from: every uploaded file, plus a "missing"
// row for each required doc type with nothing uploaded (which blocks sending).
const sendDocsFrom = (cats) =>
  cats.flatMap((c) =>
    c.reqs.flatMap((r) =>
      r.files.length ? r.files : r.required ? [{ id: "missing-" + r.docId, name: r.name, section: c.name, missing: true, docId: r.docId, parentId: r.parentId }] : []
    )
  );

// "Send to Bank" → NOA → Contract Signing, and the per-financier document
// email flow (follow-up / full application) that sits alongside it. NOA and
// Contract Signing are backed by APIs (save_noa/get_noa, save_contract/
// get_contract); for the Send stage the local draft below is the only thing
// standing between a refresh and losing this work.
export default function usePostApplication({
  setDocs,
  setAutoOpen,
  seqRef,
  selectedBanks,
  financierList,
  configFinancierList,
  fins,
  v,
  coborrowers,
  flash,
  touchSaved,
  navigate,
  displayLoanId,
  user,
  loanId,
  setLoanId,
  loadConfigIfNeeded,
  loadLeadDetails,
  postLeadId,
  onPostApplicationRoute,
}) {
  const [post, setPost] = useState(null);
  const [finTab, setFinTab] = useState(null);
  const [sent, setSent] = useState({});
  const [finModal, setFinModal] = useState(null);
  const [mail, setMail] = useState({ to: "", cc: "", subject: "", body: "", endorsementDate: "", remark: "", followUpDate: "", followUpRemark: "" });
  const [mailSending, setMailSending] = useState(false);
  const [mailLoading, setMailLoading] = useState(false);
  const [finExtra, setFinExtra] = useState({});
  const [sendExcl, setSendExcl] = useState({});
  const [noaStore, setNoaStore] = useState({});
  const [contractStore, setContractStore] = useState({});
  const [noaFieldErrors, setNoaFieldErrors] = useState({});
  const [contractFieldErrors, setContractFieldErrors] = useState({});
  const [noaOpen, setNoaOpen] = useState(null);
  const [contractOpen, setContractOpen] = useState(null);
  // Per-financier server state from get_noa, same shape as contractMeta.
  const [noaMeta, setNoaMeta] = useState({});
  const [noaSaving, setNoaSaving] = useState(false);
  const [noaExtracting, setNoaExtracting] = useState(false);
  // Per-financier server state from get_contract:
  // { [bankKey]: { state: 0 pending | 1 signed | 2 declined, editable } }
  const [contractMeta, setContractMeta] = useState({});
  const [contractSaving, setContractSaving] = useState(false);
  // get_sent_to_financier, keyed by String(financier_id) like every other
  // bank-keyed store: { raw, docs, history, lastSent, lastFollowUp }.
  const [sendInfo, setSendInfo] = useState({});
  const [sendLoading, setSendLoading] = useState(false);
  // get_lead_details for the NOA / Contract Signing tab in view.
  const [detailsLoading, setDetailsLoading] = useState(false);
  // A Send-stage document preview (these come from get_sent_to_financier, not
  // the useDocuments list the shared PreviewModal normally looks up).
  const [sendPreview, setSendPreview] = useState(null);
  // Missing required doc currently uploading from the Send page ("missing-<id>").
  const [sendUploadingId, setSendUploadingId] = useState(null);
  const [sendRemovingId, setSendRemovingId] = useState(null);
  // Banks sent to from this session, kept "sent" even before history shows it.
  const sessionSentRef = useRef(new Set());
  // A lead whose financier selection (fins/configOptions) isn't loaded into
  // appForm yet (e.g. "Track Application" clicked straight from the dashboard
  // without going through the create/continue flow first) — finishOpen runs
  // once that data actually arrives instead of reading it too early.
  const [pendingOpen, setPendingOpen] = useState(null);
  // True until the restore effect below has had its first chance to decide
  // whether landing directly on /post-application/:leadId (a refresh, or a
  // deep link) has a draft to restore — only matters for that first render,
  // where `post` is still null; without it the route below would render its
  // "nothing to restore" Navigate before the effect ever got to check.
  const [checking, setChecking] = useState(() => onPostApplicationRoute);

  const finishOpen = (rawId, name, stage, tabStatus) => {
    const banks = selectedBanks();
    // Sent status only ever comes from get_sent_to_financier history (or a
    // send made in this session) — never assumed from the stage opened at.
    const sentMap = {};

    const borrower = v.name || name || "";
    // NOA defaults only have a single co-borrower name slot — use the first
    // when there are several (this local-only mock form has no per-financier
    // multi-co-borrower concept yet).
    const coborrowerName = v.has_coborrower === "yes" ? coborrowers[0]?.name || "" : "";
    const noaS = {};
    const contractS = {};
    banks.forEach((b) => {
      noaS[b.key] = noaDefaultFor(b.name, borrower, coborrowerName);
      contractS[b.key] = contractDefaultFor();
    });

    // tabStatus (get_leads, per financier) lives on post so the draft keeps it
    // across a refresh; {} when opened from the application form instead.
    setPost({ loanId: displayLoanId, rawId, name, stage: stage || "send", done: false, declined: false, tabStatus: tabStatus || {} });
    setFinTab(banks[0] ? banks[0].key : null);
    setSent(sentMap);
    setFinModal(null);
    setAutoOpen(false);
    setNoaStore(noaS);
    setContractStore(contractS);
    setNoaOpen(banks[0] ? banks[0].key : null);
    setContractOpen(banks[0] ? banks[0].key : null);
    window.scrollTo(0, 0);
  };

  const openPost = (info) => {
    const rawId = info.rawId ?? loanId;
    navigate(`/post-application/${rawId}`);
    if (loanId === rawId && selectedBanks().length) {
      finishOpen(rawId, info.name, info.stage, info.tabStatus);
      return;
    }
    // This lead's financier selection isn't loaded into appForm yet — load it,
    // and finish opening once it lands (see the effect below).
    setLoanId(rawId);
    loadConfigIfNeeded();
    setPendingOpen({ rawId, name: info.name, stage: info.stage, tabStatus: info.tabStatus });
    loadLeadDetails(rawId);
  };

  // Finishes openPost once appForm has caught up to the requested lead, and
  // also covers landing directly on /post-application/:leadId (refresh, or a
  // "Track Application" deep link) with no local draft to restore from.
  useEffect(() => {
    if (pendingOpen) {
      // loadConfigIfNeeded (financier_list) and loadLeadDetails (fins) are two
      // independent requests — wait for BOTH before reading selectedBanks(),
      // otherwise finishOpen can run the instant fins lands while financier_list
      // is still in flight, initializing every bank-keyed store as empty and
      // never revisiting it (this is what "financiers don't show up" was).
      if (loanId !== pendingOpen.rawId || !selectedBanks().length) return;
      finishOpen(pendingOpen.rawId, pendingOpen.name, pendingOpen.stage, pendingOpen.tabStatus);
      setPendingOpen(null);
      setChecking(false);
      return;
    }
    if (post || !onPostApplicationRoute || !postLeadId) return;

    const draft = getPostApplicationDraft();
    if (draft && Number(draft.loanId) === postLeadId) {
      setPost(draft.post || null);
      setFinTab(draft.finTab || null);
      setSent(draft.sent || {});
      setFinExtra(draft.finExtra || {});
      setSendExcl(draft.sendExcl || {});
      setNoaStore(draft.noaStore || {});
      setContractStore(draft.contractStore || {});
      if (loanId !== postLeadId) {
        setLoanId(postLeadId);
        loadConfigIfNeeded();
        loadLeadDetails(postLeadId);
      }
      setChecking(false);
      return;
    }
    // No draft for this lead (different browser/device, or storage was
    // cleared) — nothing to safely reconstruct without a save API behind
    // this flow, so send them back rather than fabricating a stage/name.
    setChecking(false);
    navigate("/dashboard", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpen, loanId, fins, configFinancierList, post, onPostApplicationRoute, postLeadId]);

  // Persists the whole post-application draft on every relevant change so a
  // refresh restores it — see the restore effect above.
  useEffect(() => {
    if (!post) return;
    savePostApplicationDraft({
      loanId: post.rawId,
      post,
      finTab,
      sent,
      finExtra,
      sendExcl,
      noaStore,
      contractStore,
    });
  }, [post, finTab, sent, finExtra, sendExcl, noaStore, contractStore]);

  // Pulls one financier's saved NOA and contract in a single get_lead_details
  // call (tab_name noa_received + contract_signed_details — same payloads as
  // get_noa / get_contract). Recorded answers lock the form and the server
  // copy wins over any stale local draft — including its signed document URL,
  // which is what the upload thumbnail shows. If the server has no document
  // yet, the one just uploaded here is kept rather than blanked.
  const loadSavedDetails = (bk, isCancelled = () => false) => {
    const bankName = (selectedBanks().find((b) => b.key === bk) || {}).name;
    setDetailsLoading(true);
    return getLeadDetails(Number(post.rawId), ["noa_received", "contract_signed_details"], Number(bk))
      .then(({ data: body }) => {
        if (isCancelled() || body?.status !== 200 || !body.data) return;
        const noa = body.data.noa_received;
        if (noa) {
          const state = noa.is_noa_approved ?? 0;
          setNoaMeta((m) => ({ ...m, [bk]: { state, editable: noa.editable !== false } }));
          if (state !== 0) {
            setNoaStore((st) => {
              const saved = noaFromApi(noa);
              const keepUpload = !saved.noa_upload && st[bk]?.noa_upload ? { noa_upload: st[bk].noa_upload } : {};
              const merged = { ...noaDefaultFor(bankName, "", ""), ...keepUpload, ...saved };
              // Still editable (or updatable) → today's count; a locked NOA keeps the saved one.
              const contractSigned = (body.data.contract_signed_details?.is_contract_signed_approved ?? 0) === 1;
              const openForEdit = noa.editable !== false || (state === 1 && !contractSigned);
              return { ...st, [bk]: openForEdit ? withDeadlineDays(merged) : merged };
            });
          }
        }
        const contract = body.data.contract_signed_details;
        if (contract) {
          const state = contract.is_contract_signed_approved ?? 0;
          setContractMeta((m) => ({ ...m, [bk]: { state, editable: contract.editable !== false } }));
          setContractStore((st) => {
            const saved = contractFromApi(contract);
            const keepUpload =
              !saved.contract_signed_docs && st[bk]?.contract_signed_docs ? { contract_signed_docs: st[bk].contract_signed_docs } : {};
            return { ...st, [bk]: { ...(state !== 0 ? contractDefaultFor() : st[bk]), ...keepUpload, ...saved } };
          });
        }
      })
      .catch((err) => console.error("get_lead_details (post-application) failed", err.response?.status, err.response?.data || err.message))
      .finally(() => !isCancelled() && setDetailsLoading(false));
  };

  // NOA Received defaults to "Yes" for every financier with no recorded answer
  // yet — also covers stores restored from an older draft (blank value) and
  // banks that never got seeded, which would otherwise show neither option.
  useEffect(() => {
    if (!post) return;
    const missing = selectedBanks().filter((b) => !noaMeta[b.key]?.state && !noaStore[b.key]?.noa_received);
    if (!missing.length) return;
    const borrower = post.name || v.name || "";
    const coborrowerName = v.has_coborrower === "yes" ? coborrowers[0]?.name || "" : "";
    setNoaStore((st) => {
      const next = { ...st };
      missing.forEach((b) => {
        next[b.key] = { ...noaDefaultFor(b.name, borrower, coborrowerName), ...st[b.key], noa_received: "Yes" };
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, noaStore, noaMeta, selectedBanks().length]);

  // Loads only the financier on the active tab — on entering the NOA /
  // Contract Signing stage, and again whenever another tab is picked (and on
  // each stage change, so Contract picks up a NOA just saved).
  const trackedStage = post && !post.done && (post.stage === "noa" || post.stage === "contract") ? post.stage : null;
  const firstBankKey = post ? selectedBanks()[0]?.key || "" : "";
  const trackedBankKey = trackedStage ? (trackedStage === "noa" ? noaOpen : contractOpen) || firstBankKey : "";
  useEffect(() => {
    if (!trackedStage || !trackedBankKey) return;
    let cancelled = false;
    loadSavedDetails(trackedBankKey, () => cancelled);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedStage, trackedBankKey, post?.rawId]);

  // Loads every financier's send state on entering the Send stage. A financier
  // with a "sent_to_financier" history entry counts as sent; the local `sent`
  // flag is only kept on top of that for a send made in this session.
  const onSendStage = !!post && !post.done && post.stage === "send";
  // One call per financier (financier_id is required) — callers pass just the
  // active tab's key. Resolves to the
  // entries it loaded — { [bankKey]: info } — and merges them into sendInfo.
  const loadSendInfo = (keys, isCancelled = () => false) => {
    setSendLoading(true);
    const loadOne = (key) =>
      getSentToFinancier(Number(post.rawId), Number(key))
        .then(({ data: body }) => {
          if (body?.status !== 200) {
            console.error("get_sent_to_financier returned", body?.status, body?.message);
            return null;
          }
          const list = Array.isArray(body.data) ? body.data : body.data ? [body.data] : [];
          const f = list.find((x) => String(x.financier_id) === String(key)) || list[0];
          if (!f) return null;
          // Newest first by sent_at (not trusting the API's order); anything
          // that isn't a follow-up counts as a send to the financier.
          const history = [...(f.history || [])].sort((x, y) => new Date(y.sent_at || 0) - new Date(x.sent_at || 0));
          const cats = sendCatsFrom(f.document_categories, key);
          return {
            raw: f,
            cats,
            docs: sendDocsFrom(cats),
            history,
            lastSent: history.find((h) => h.template_for !== "follow_up_email") || null,
            lastFollowUp: history.find((h) => h.template_for === "follow_up_email") || null,
          };
        })
        .catch((err) => {
          console.error("get_sent_to_financier failed", err.response?.status, err.response?.data || err.message);
          return null;
        });
    return Promise.all(keys.map(loadOne))
      .then((results) => {
        if (isCancelled()) return null;
        const next = {};
        keys.forEach((k, i) => results[i] && (next[k] = results[i]));
        setSendInfo((st) => {
          const merged = { ...st };
          // Keep an email sent from this session if the server's history
          // hasn't caught up with it yet.
          Object.entries(next).forEach(([k, info]) => {
            const prev = st[k] || {};
            merged[k] = {
              ...info,
              lastSent: info.lastSent || prev.lastSent || null,
              lastFollowUp: info.lastFollowUp || prev.lastFollowUp || null,
            };
          });
          return merged;
        });
        // The server's history decides "sent" for every bank it returned —
        // including un-marking a stale local flag — except a send made in
        // this session that the history doesn't show yet.
        setSent((s) => {
          const merged = { ...s };
          Object.entries(next).forEach(([k, info]) => {
            merged[k] = !!info.lastSent || sessionSentRef.current.has(k);
          });
          return merged;
        });
        return next;
      })
      .finally(() => !isCancelled() && setSendLoading(false));
  };
  // Same for Send to Bank: the active tab's financier only, re-read on every
  // tab switch.
  const sendBankKey = onSendStage ? finTab || firstBankKey : "";
  useEffect(() => {
    if (!sendBankKey) return;
    let cancelled = false;
    loadSendInfo([sendBankKey], () => cancelled);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendBankKey, post?.rawId]);

  // Uploads a document (required or optional) straight from the Send page, the same
  // upload_doc call the Upload Documents step makes (doc_id + parent_id; the
  // bank-specific Home Loan Application Form, doc_id 2, also takes the
  // financier_id). Then re-reads that bank so the row shows as uploaded.
  // Documents are final for a bank once it has been sent to.
  const sentTo = (key) => !!sent[key] || stageState(post?.tabStatus, key, "send") === "complete";
  const uploadMissingSendDoc = async (key, doc, file) => {
    if (!file || sendUploadingId) return;
    if (sentTo(key)) {
      flash("Already sent to this bank — documents can no longer be changed");
      return;
    }
    const formData = new FormData();
    formData.append("images", file);
    formData.append("lead_id", post.rawId);
    formData.append("doc_id", doc.docId);
    if (doc.parentId != null) formData.append("parent_id", doc.parentId);
    formData.append("upload_type", "manual");
    if (Number(doc.docId) === HL_FORM_DOC_ID) formData.append("financier_id", key);
    setSendUploadingId(doc.id);
    try {
      const { data } = await uploadDocApi(formData);
      if (!isApiSuccess(data)) {
        flash(data?.message || "Upload failed, please try again");
        return;
      }
      flash("Uploaded: " + doc.name);
      touchSaved();
      await loadSendInfo([key]);
    } catch (err) {
      console.error("upload_doc (send page) failed", err.response?.status, err.response?.data || err.message);
      flash(err.response?.data?.message || "Upload failed, please try again");
    } finally {
      setSendUploadingId(null);
    }
  };

  // Deletes an uploaded file from the Send page — save_docs soft-delete by its
  // rem_lead_docs id (the get_sent_to_financier image id) — then re-reads that
  // bank, so a doc type at its upload limit can take a new file again.
  const removeSendDoc = async (key, file) => {
    if (!file || sendRemovingId) return;
    if (sentTo(key)) {
      flash("Already sent to this bank — documents can no longer be changed");
      return;
    }
    setSendRemovingId(file.id);
    try {
      const { data } = await removeDocs(Number(post.rawId), [Number(file.id)]);
      if (!isApiSuccess(data)) {
        flash(data?.message || "Could not remove document");
        return;
      }
      flash("Document removed");
      touchSaved();
      await loadSendInfo([key]);
    } catch (err) {
      console.error("save_docs remove (send page) failed", err.response?.status, err.response?.data || err.message);
      flash(err.response?.data?.message || "Could not remove document");
    } finally {
      setSendRemovingId(null);
    }
  };

  // Uploads a post-application document (NOA / signed contract) and resolves
  // to its URL, which is what save_noa / save_contract take.
  const uploadPostDoc = (field, file) =>
    uploadFileToStorage(file, post?.rawId).catch((err) => {
      console.error("docs_upload failed", err.response?.status, err.response?.data || err.message);
      flash("Upload failed, please try again");
      return null;
    });

  // NOA upload: same upload, then OCR straight away to pre-fill the form.
  const uploadNoaDoc = (field, file) => {
    const banks = selectedBanks();
    const bk = noaOpen || (banks[0] && banks[0].key);
    return uploadPostDoc(field, file).then((url) => {
      if (url && field.name === "noa_upload" && bk) extractNoa(bk, url);
      return url;
    });
  };

  // After "Add bank" on the Send page: seed the new banks' NOA / contract
  // forms the way finishOpen does for the original ones, and open the first.
  const onBanksAdded = (keys) => {
    if (!keys.length) return;
    const borrower = post?.name || v.name || "";
    const coborrowerName = v.has_coborrower === "yes" ? coborrowers[0]?.name || "" : "";
    const nameOf = (k) => {
      const f = financierList().find((x) => String(x.id) === k);
      return f ? financierDisplayName(f) : "";
    };
    setNoaStore((st) => ({ ...Object.fromEntries(keys.map((k) => [k, noaDefaultFor(nameOf(k), borrower, coborrowerName)])), ...st }));
    setContractStore((st) => ({ ...Object.fromEntries(keys.map((k) => [k, contractDefaultFor()])), ...st }));
    setFinTab(keys[0]);
  };

  const leavePostApplication = () => {
    clearPostApplicationDraft();
    setPost(null);
    navigate("/dashboard");
  };

  const setStage = (stg) => {
    setPost((p) => ({ ...p, stage: stg }));
    window.scrollTo(0, 0);
  };

  // Editing a field clears its own error; the full check runs again on save.
  const clearBankFieldError = (setErrors, bk, fk) =>
    setErrors((e) => {
      if (!e[bk] || !e[bk][fk]) return e;
      const { [fk]: _gone, ...rest } = e[bk];
      return { ...e, [bk]: rest };
    });

  const setNoaField = (bk, fk, val) => {
    setNoaStore((s) => {
      const next = { ...s[bk], [fk]: val };
      if (fk === "approval_valid_until") next.post_approval_deadline_days = daysUntil(val);
      return { ...s, [bk]: next };
    });
    clearBankFieldError(setNoaFieldErrors, bk, fk);
    touchSaved();
  };
  const setContractField = (bk, fk, val) => {
    setContractStore((s) => ({ ...s, [bk]: { ...s[bk], [fk]: val } }));
    clearBankFieldError(setContractFieldErrors, bk, fk);
    touchSaved();
  };

  const attachExtra = (key, name) => {
    seqRef.current += 1;
    const id = "x" + seqRef.current;
    const doc = {
      id,
      name: name.replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "") + ".pdf",
      size: "256 KB",
      from: "this computer",
      when: "Just now",
      kind: "PDF",
      prev: "placeholder-document-doc.png",
    };
    setFinExtra((s) => ({ ...s, [key + "::" + name]: id }));
    setDocs((st) => [...st, doc]);
    touchSaved();
    flash("Attached: " + name);
  };

  // The first send and the follow-up are two different emails on the API side:
  // financier_email / financier_cc_email / subject / template for the send,
  // follow_up_email_id / follow_up_cc_email_id / follow_up_subject /
  // follow_up_template for the reminder. Everything comes from
  // get_sent_to_financier — there is no local draft. Endorsement date / remark
  // (send) and the follow-up remark are prefilled from what was saved last.
  // Once an email has gone out, its latest history entry (what was actually
  // sent: recipients, subject, send_html_template) replaces the template.
  const mailFor = (kind, info) => {
    const api = info?.raw;
    const extras = {
      endorsementDate: isoDate(api?.endorsement_date) || todayIso(),
      remark: api?.remark || "",
      followUpDate: todayIso(),
      followUpRemark: api?.follow_up_remark || "",
    };
    if (!api) return { ...extras, to: "", cc: "", subject: "", body: "" };
    const last = kind === "followup" ? info.lastFollowUp : info.lastSent;
    if (last) {
      return {
        ...extras,
        to: last.financier_email || "",
        cc: last.financier_cc_email || "",
        subject: last.subject || "",
        body: templateToText(last.send_html_template),
      };
    }
    return kind === "followup"
      ? {
          ...extras,
          to: api.follow_up_email_id || api.financier_email || "",
          cc: api.follow_up_cc_email_id || "",
          subject: api.follow_up_subject || api.subject || "",
          body: templateToText(api.follow_up_template),
        }
      : {
          ...extras,
          to: api.financier_email || "",
          cc: api.financier_cc_email || "",
          subject: api.subject || "",
          body: templateToText(api.template),
        };
  };

  // Uses the data the tab already loaded; only fetches if that hasn't landed.
  const openMail = async (kind, key) => {
    setFinModal({ kind, key });
    let info = sendInfo[key];
    if (!info) {
      setMail(mailFor(kind, null));
      setMailLoading(true);
      const fresh = await loadSendInfo([key]);
      setMailLoading(false);
      info = fresh?.[key];
    }
    if (!info) {
      flash("Could not load the email template for this bank");
      return;
    }
    setMail(mailFor(kind, info));
  };
  // Both emails go through sent_to_financier (no follow_up_email = first
  // send, follow_up_email "Yes" = follow-up) with the documents still ticked on the Send page.
  // History and the Sent state are then re-read from get_sent_to_financier.
  const sendMail = async () => {
    if (!finModal || mailSending || mailLoading) return;
    const key = finModal.key;
    const followup = finModal.kind === "followup";
    // The send page disables its buttons too; this is the last guard before
    // sent_to_financier, which fails when a required document is missing.
    if (!followup && stageState(post.tabStatus, key, "send") === "locked") {
      flash("Sending to this bank isn't available yet");
      return;
    }
    const missingCount = (sendInfo[key]?.docs || []).filter((d) => d.missing).length;
    if (missingCount) {
      flash("Upload all required documents before sending (" + missingCount + " missing)");
      return;
    }
    if (!mail.to.trim() || !mail.subject.trim() || !mail.body.trim()) {
      flash("To, subject and message are required");
      return;
    }
    if (followup ? !mail.followUpDate : !mail.endorsementDate) {
      flash(followup ? "Follow-up date is required" : "Endorsement date is required");
      return;
    }
    // doc_ids are document_list doc_ids (one per doc type), not file row ids.
    const docIds = [
      ...new Set(
        (sendInfo[key]?.docs || [])
          .filter((d) => !d.missing && !sendExcl[key + ":" + d.id] && d.docId != null)
          .map((d) => Number(d.docId))
      ),
    ];
    const payload = {
      lead_id: Number(post.rawId),
      financier_id: Number(key),
      send_html_template: textToHtml(mail.body),
      user_email: mail.to.trim(),
      user_cc_email: mail.cc.trim(),
      subject: mail.subject.trim(),
      doc_ids: docIds,
      // The first send carries endorsement_date / stf_remark; the follow-up is
      // flagged with follow_up_email "Yes" and has its own date and remark.
      ...(followup
        ? { follow_up_email: "Yes", follow_up_date: mail.followUpDate, stf_follow_up_remark: mail.followUpRemark.trim() }
        : { endorsement_date: mail.endorsementDate, stf_remark: mail.remark.trim() }),
    };

    const finRaw = financierList().find((f) => String(f.id) === key);
    const name = finRaw ? financierDisplayName(finRaw) : "";
    setMailSending(true);
    try {
      const { data: body } = await saveSentToFinancier(payload);
      if (body?.status !== 200) {
        flash(body?.message || "Could not send email");
        return;
      }
      if (!followup) {
        sessionSentRef.current.add(key);
        setSent((s) => ({ ...s, [key]: true }));
        setPost((p) => ({ ...p, tabStatus: withStageDone(p.tabStatus, key, "send", "approved") }));
      }
      const entry = {
        sent_at: new Date().toISOString(),
        template_for: followup ? "follow_up_email" : "sent_to_financier",
        financier_email: payload.user_email,
        financier_cc_email: payload.user_cc_email,
        subject: payload.subject,
        send_html_template: payload.send_html_template,
        documents: docIds.map((id) => ({ id })),
      };
      setSendInfo((st) =>
        st[key] ? { ...st, [key]: { ...st[key], [followup ? "lastFollowUp" : "lastSent"]: entry } } : st
      );
      setFinModal(null);
      touchSaved();
      loadSendInfo([key]);
      flash(body.message || (followup ? "Follow-up sent to " + name : "Sent to " + name));
    } catch (err) {
      console.error("sent_to_financier failed", err.response?.status, err.response?.data || err.message);
      flash(err.response?.data?.message || "Could not send email");
    } finally {
      setMailSending(false);
    }
  };

  // "Extract details": runs the NOA file / email screenshot on the active tab
  // through OCR and fills whatever fields it read, for the user to review
  // before Save NOA.
  // Runs on its own right after a NOA upload (uploadNoaDoc); the button only
  // retries. bk/url are passed in there since the store hasn't caught up yet.
  const extractNoa = async (bkArg, urlArg) => {
    const banks = selectedBanks();
    const bk = (typeof bkArg === "string" && bkArg) || noaOpen || (banks[0] && banks[0].key);
    const docPath = String((typeof urlArg === "string" && urlArg) || noaStore[bk]?.noa_upload || "").split("?")[0];
    if (!bk || noaExtracting) return;
    if (!/^https?:/.test(docPath)) {
      flash("Upload the NOA or a screenshot of the NOA email first");
      return;
    }
    setNoaExtracting(true);
    try {
      const { data: body } = await extractNoaApi({ lead_id: Number(post.rawId), financier_id: Number(bk), doc_path: docPath });
      const fields = body?.status === 200 ? body.data?.fields || {} : {};
      const names = Object.keys(fields);
      if (!names.length) {
        flash(body?.message || "Could not read NOA details from this file");
        return;
      }
      setNoaStore((s) => ({ ...s, [bk]: withDeadlineDays({ ...s[bk], ...fields }) }));
      setNoaFieldErrors((e) => {
        if (!e[bk]) return e;
        const rest = { ...e[bk] };
        names.forEach((n) => delete rest[n]);
        return { ...e, [bk]: rest };
      });
      touchSaved();
      flash(names.length + " field" + (names.length > 1 ? "s" : "") + " filled from the NOA — please review before saving");
    } catch (err) {
      console.error("extract_noa failed", err.response?.status, err.response?.data || err.message);
      flash(err.response?.data?.message || "Could not read NOA details from this file");
    } finally {
      setNoaExtracting(false);
    }
  };

  // A received (approved) NOA stays open for updates until a contract is
  // signed with that bank, whatever the completed tab / editable flag say.
  // A declined NOA stays final.
  const noaUpdatable = (bk) => {
    if (!bk) return false;
    const received = noaMeta[bk]?.state === 1 || (noaMeta[bk]?.state == null && stageState(post?.tabStatus, bk, "noa") === "complete");
    const signed = contractMeta[bk]?.state === 1 || stageState(post?.tabStatus, bk, "contract") === "complete";
    return received && !signed;
  };

  // Saves the NOA for the financier on the active tab (save_noa is one
  // financier per call). Once every financier has an answer, a single approval
  // moves on to Contract Signing; all declined ends the flow.
  const saveNoa = async () => {
    const banks = selectedBanks();
    const bk = noaOpen || (banks[0] && banks[0].key);
    if (!bk || noaSaving) return;
    // A received NOA with no contract signed yet re-saves ("Update NOA
    // Details") past the completed / not-editable lock.
    const isUpdate = noaUpdatable(bk);
    if (noaMeta[bk]?.editable === false && !isUpdate) {
      flash("NOA for this financier can no longer be changed");
      return;
    }
    const noaTab = stageState(post.tabStatus, bk, "noa");
    if ((noaTab === "complete" && !isUpdate) || noaTab === "locked") {
      flash(noaTab === "complete" ? "NOA is already completed for this bank" : "NOA isn't available for this bank yet");
      return;
    }
    const values = noaStore[bk] || {};
    const errs = validateFields(NOA_FIELDS_JSON, values);
    if (Object.keys(errs).length) {
      setNoaFieldErrors((e) => ({ ...e, [bk]: errs }));
      flash("Please fix the highlighted fields");
      return;
    }
    setNoaFieldErrors((e) => ({ ...e, [bk]: {} }));

    const approved = values.noa_received === "Yes";
    setNoaSaving(true);
    try {
      const { data: body } = await saveNoaApi(noaPayload(Number(post.rawId), Number(bk), values));
      if (body?.status !== 200) {
        flash(body?.message || "Could not save NOA");
        return;
      }
      // A decline is terminal; an approval can still be edited (once) or
      // changed to a decline until a contract is signed.
      const nextMeta = { ...noaMeta, [bk]: { state: approved ? 1 : 2, editable: approved } };
      setNoaMeta(nextMeta);
      setPost((p) => ({ ...p, tabStatus: withStageDone(p.tabStatus, bk, "noa", approved ? "approved" : "rejected") }));
      touchSaved();
      // Show exactly what was stored (signed document URL, lock state).
      loadSavedDetails(bk);
      flash(body.message || (isUpdate ? "NOA details updated" : approved ? "NOA saved" : "Decline recorded"));
      // An update stays on this bank's tab — the other banks' answers and the
      // stage flow were already settled when it was first saved.
      if (isUpdate) {
        if (!approved && !banks.some((b) => nextMeta[b.key]?.state === 1)) {
          setPost((p) => ({ ...p, done: true, declined: true, declinedAt: "noa" }));
          window.scrollTo(0, 0);
        }
        return;
      }

      const pending = banks.find((b) => !nextMeta[b.key]?.state);
      if (pending) {
        setNoaOpen(pending.key);
        return;
      }
      if (banks.some((b) => nextMeta[b.key]?.state === 1)) {
        setStage("contract");
      } else {
        setPost((p) => ({ ...p, done: true, declined: true, declinedAt: "noa" }));
        window.scrollTo(0, 0);
      }
    } catch (err) {
      console.error("save_noa failed", err.response?.status, err.response?.data || err.message);
      flash(err.response?.data?.message || "Could not save NOA");
    } finally {
      setNoaSaving(false);
    }
  };

  // Saves the financier on the active tab only — save_contract is one
  // financier per call, and a lead can only be signed with one of them.
  const saveContract = async () => {
    const banks = selectedBanks();
    const bk = contractOpen || (banks[0] && banks[0].key);
    if (!bk || contractSaving) return;
    if (contractMeta[bk]?.editable === false) {
      flash("Contract signing for this financier can no longer be changed");
      return;
    }
    const contractTab = stageState(post.tabStatus, bk, "contract");
    if (contractTab === "complete" || contractTab === "locked") {
      flash(contractTab === "complete" ? "Contract signing is already completed for this bank" : "Contract signing isn't available for this bank yet");
      return;
    }
    const values = contractStore[bk] || {};
    const errs = validateFields(CONTRACT_FIELDS_JSON, values);
    if (Object.keys(errs).length) {
      setContractFieldErrors((e) => ({ ...e, [bk]: errs }));
      flash("Please fix the highlighted fields");
      return;
    }
    setContractFieldErrors((e) => ({ ...e, [bk]: {} }));

    const signed = values.contract_signing_received === "Yes";
    const days = values.days_since_approval;
    const payload = {
      lead_id: Number(post.rawId),
      financier_id: Number(bk),
      contract_signing_received: values.contract_signing_received,
      ...(days !== "" && days != null ? { days_since_approval: Number(days) } : {}),
      ...(signed
        ? { contract_signing_date: values.contract_signing_date, contract_signed_docs: values.contract_signed_docs }
        : { decline_remark: values.decline_remark }),
    };

    setContractSaving(true);
    try {
      const { data: body } = await saveContractApi(payload);
      if (body?.status !== 200) {
        flash(body?.message || "Could not save contract signing");
        return;
      }
      // Either outcome locks this financier (get_contract reports editable: false).
      const nextMeta = { ...contractMeta, [bk]: { state: signed ? 1 : 2, editable: false } };
      setContractMeta(nextMeta);
      setPost((p) => ({ ...p, tabStatus: withStageDone(p.tabStatus, bk, "contract", signed ? "approved" : "rejected") }));
      touchSaved();
      loadSavedDetails(bk);

      if (signed) {
        setPost((p) => ({ ...p, done: true, declined: false }));
        window.scrollTo(0, 0);
        flash(body.message || "Contract signing recorded");
        return;
      }
      const pending = banks.find((b) => (nextMeta[b.key]?.state ?? 0) !== 2);
      if (!pending) {
        setPost((p) => ({ ...p, done: true, declined: true, declinedAt: "contract" }));
        window.scrollTo(0, 0);
      } else {
        setContractOpen(pending.key);
      }
      flash(body.message || "Contract decline recorded");
    } catch (err) {
      console.error("save_contract failed", err.response?.status, err.response?.data || err.message);
      flash(err.response?.data?.message || "Could not save contract signing");
    } finally {
      setContractSaving(false);
    }
  };

  return {
    post,
    // True while openPost is still waiting on a lead's financier selection to
    // load (loadConfigIfNeeded/loadLeadDetails), or the very first render of a
    // direct /post-application/:leadId hit is still checking for a draft to
    // restore — the route below renders a loader instead of bouncing to
    // /dashboard during this window.
    opening: !!pendingOpen || checking,
    setPost,
    finTab,
    setFinTab,
    sent,
    sendInfo,
    sendLoading,
    detailsLoading,
    sendPreview,
    setSendPreview,
    sendUploadingId,
    sendRemovingId,
    removeSendDoc,
    uploadMissingSendDoc,
    onBanksAdded,
    finModal,
    setFinModal,
    mail,
    setMail,
    mailSending,
    mailLoading,
    finExtra,
    sendExcl,
    setSendExcl,
    noaStore,
    contractStore,
    noaFieldErrors,
    contractFieldErrors,
    noaOpen,
    setNoaOpen,
    contractOpen,
    setContractOpen,
    noaMeta,
    noaSaving,
    noaExtracting,
    extractNoa,
    noaUpdatable,
    contractMeta,
    contractSaving,
    uploadPostDoc,
    uploadNoaDoc,
    openPost,
    leavePostApplication,
    setStage,
    setNoaField,
    setContractField,
    attachExtra,
    openMail,
    sendMail,
    saveNoa,
    saveContract,
  };
}
